import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { checkActivationToken, activateAccount } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Check, AlertCircle, Loader2, DoorOpen } from 'lucide-react';

export default function ActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) { setError('No activation token provided'); setLoading(false); return; }
    checkActivationToken(token)
      .then(data => {
        if (data.account_status === 'active') { setError('This account has already been activated.'); }
        else { setTokenData(data); }
      })
      .catch(() => setError('Invalid or expired activation link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (password.length < 6) { setFormError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setFormError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await activateAccount(token, password);
      setDone(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Activation failed. Please try again.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <Helmet><title>Activate Account | Go Freestyle Studios</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Header */}
      <div className="px-6 py-6">
        <Link to="/" className="flex items-center" data-testid="brand-link">
          <img src="/logo.webp" alt="Go Freestyle Studios" className="h-[52px]" style={{filter:'contrast(1.15) brightness(0.95)'}} />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">

          {/* Error state */}
          {error && !done && (
            <div className="text-center" data-testid="activation-error">
              <div className="w-14 h-14 rounded-2xl bg-[#FDF2F2] flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-[#B33A3A]" strokeWidth={1.5} />
              </div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Activation Issue</h1>
              <p className="text-sm text-[#5C5C55] mb-6">{error}</p>
              <Link to="/login" className="text-sm text-[#3A4035] font-medium hover:underline" data-testid="go-to-login">
                Go to sign in
              </Link>
            </div>
          )}

          {/* Success state */}
          {done && (
            <div className="text-center animate-fade-in" data-testid="activation-success">
              <div className="w-14 h-14 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-[#3A4035]" strokeWidth={1.5} />
              </div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Account Activated</h1>
              <p className="text-sm text-[#5C5C55] mb-6">
                Your account is ready. You can now sign in and access your studio dashboard.
              </p>
              <Button onClick={() => navigate('/login')}
                className="bg-[#3A4035] text-white rounded-full px-8 py-3 text-sm font-medium hover:bg-[#2B3027]"
                data-testid="go-to-login-btn">
                Sign In
              </Button>
            </div>
          )}

          {/* Activation form */}
          {tokenData && !done && !error && (
            <div className="animate-fade-in" data-testid="activation-form">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-4">
                  <DoorOpen className="w-7 h-7 text-[#3A4035]" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-2">
                  Welcome, {tokenData.name}
                </h1>
                {tokenData.studio_name && (
                  <p className="text-sm text-[#3A4035] font-medium mb-1">
                    {tokenData.studio_name}{tokenData.site_name ? ` at ${tokenData.site_name}` : ''}
                  </p>
                )}
                <p className="text-sm text-[#5C5C55]">
                  Set your password to activate your account
                </p>
              </div>

              {/* Email display */}
              <div className="bg-[#F2F0EB] rounded-xl px-4 py-3 mb-6">
                <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-0.5">Your Email</p>
                <p className="text-sm font-medium text-[#1A1A1A]" data-testid="activation-email">{tokenData.email}</p>
              </div>

              {formError && (
                <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4] mb-4" data-testid="form-error">
                  <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
                  <p className="text-xs text-[#B33A3A]">{formError}</p>
                </div>
              )}

              <form onSubmit={handleActivate} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                    <Input id="password" type="password" value={password} onChange={e => { setPassword(e.target.value); setFormError(''); }}
                      placeholder="Choose a password (min 6 chars)"
                      className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                      data-testid="activation-password" autoFocus />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                    <Input id="confirm" type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setFormError(''); }}
                      placeholder="Confirm your password"
                      className={`pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85] ${confirm && password !== confirm ? 'border-[#B33A3A]' : ''}`}
                      data-testid="activation-confirm" />
                  </div>
                  {confirm && password !== confirm && (
                    <p className="text-[10px] text-[#B33A3A]">Passwords do not match</p>
                  )}
                </div>

                <Button type="submit" disabled={saving || !password || !confirm}
                  className="w-full bg-[#3A4035] text-white rounded-full px-6 py-3 h-12 font-medium hover:bg-[#2B3027] transition-colors"
                  data-testid="activate-submit">
                  {saving ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" />Activating...</>
                  ) : (
                    <>Activate Account</>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
